import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { LogoIcon } from "../Icons";
import { AlertCircle, Loader2 } from "lucide-react";
import useAuthStore from "../../store/authStore";
import { loginAPI } from "../../services/authService";
import { loginSchema } from "../../utils/validationSchemas";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, clearError, initializeAuth } = useAuthStore();
  const { t } = useLanguage();

  // Restore any saved session when this page mounts.
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Redirect authenticated users away from /login.
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setStatus },
  ) => {
    try {
      setStatus(null);
      clearError();

      const response = await loginAPI(values);
      if (response.success) {
        const user = response.data.user;
        login(user);
        navigate("/admin", { replace: true });
        toast.success(response.msg || "Logged in successfully!");
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (err) {
      setStatus({ error: err.message || "Invalid email or password" });

      // Clear password field on error for security
      setErrors({ password: " " });
      toast.error(err.message || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 pt-32">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 space-y-8 animate-in fade-in zoom-in duration-300">
        <div className="text-center space-y-4">
          <LogoIcon className="h-10 w-auto text-subidha-blue mx-auto" />
          <h2 className="text-3xl font-serif-brand font-black text-slate-900">
            {t("auth.login.title")}
          </h2>
          <p className="text-slate-500 font-medium">
            {t("auth.login.subtitle")}
          </p>
        </div>

        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          validationSchema={loginSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ isSubmitting, status, touched, errors }) => (
            <Form className="space-y-6">
              {status?.error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">
                      {t("auth.login.loginFailed")}
                    </p>
                    <p className="text-xs text-red-700 mt-1">{status.error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1"
                >
                  {t("auth.login.email")}
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  disabled={isSubmitting}
                  className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl focus:ring-4 focus:ring-accent-green/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    touched.email && errors.email
                      ? "border-red-300"
                      : "border-slate-200"
                  }`}
                  placeholder="admin@example.com"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-xs text-red-600 mt-1 px-1"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label
                    htmlFor="password"
                    className="text-[10px] font-black text-slate-400 uppercase tracking-widest"
                  >
                    {t("auth.login.password")}
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-[10px] font-black text-subidha-blue uppercase tracking-widest hover:text-accent-green"
                    tabIndex={-1}
                  >
                    {t("auth.login.forgotPassword")}
                  </Link>
                </div>
                <Field
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl focus:ring-4 focus:ring-accent-green/20 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    touched.password && errors.password
                      ? "border-red-300"
                      : "border-slate-200"
                  }`}
                  placeholder="••••••••"
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-xs text-red-600 mt-1 px-1"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-subidha-blue hover:bg-slate-900 text-white py-5 rounded-2xl font-black transition-all shadow-xl shadow-blue-900/10 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t("common.loading")}</span>
                  </>
                ) : (
                  <span>{t("auth.login.loginButton")}</span>
                )}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginPage;
